import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import styled from 'styled-components';

import AuthenticatedErrorNavigation from '../../Navigation/AuthenticatedErrorNavigation';

const TechnicalDetailsPre = styled.pre`
  max-height: 200px;
  overflow-y: auto;
  background: #f8f9fa;
  font-size: 0.8rem;
  padding: 0.5rem;
  border-radius: 4px;
  margin-top: 0.5rem;
  margin-bottom: 0;
`;

/**
 * Layout-specific fallback UI for locally caught render errors.
 * @param {object} props - Component props.
 * @param {Error|null} props.error - Captured render error.
 * @param {object|null} props.errorInfo - Captured React component stack details.
 * @returns {React.ReactNode} layout fallback UI.
 */
function LayoutErrorFallback({ error = null, errorInfo = null }) {
  const intl = useIntl();
  const [showTechnicalDetails, setShowTechnicalDetails] = React.useState(false);

  const toggleTechnicalDetails = () => {
    setShowTechnicalDetails((previousShowTechnicalDetails) => !previousShowTechnicalDetails);
  };

  const header = intl.formatMessage({
    id: 'layout.layout_error_boundary.layout_error_fallback.header.server.error.label',
    defaultMessage: 'Server error',
  });
  const showDetailsLabel = intl.formatMessage({
    id: 'layout.layout_error_boundary.layout_error_fallback.show_details.button.label',
    defaultMessage: 'Show details',
  });
  const hideDetailsLabel = intl.formatMessage({
    id: 'layout.layout_error_boundary.layout_error_fallback.hide_details.button.label',
    defaultMessage: 'Hide details',
  });
  const errorStackLabel = intl.formatMessage({
    id: 'layout.layout_error_boundary.layout_error_fallback.header.error_stack.label',
    defaultMessage: 'Error Stack',
  });
  const componentStackLabel = intl.formatMessage({
    id: 'layout.layout_error_boundary.layout_error_fallback.header.component_stack.label',
    defaultMessage: 'Component Stack',
  });

  return (
    <div className="flex flex-column">
      <div className="flex-auto">
        <AuthenticatedErrorNavigation />
        <div className="container text-center py-4">
          <h1>{header}</h1>
          <div className="mb-3">
            <Button
              aria-controls="layout-error-details"
              aria-expanded={showTechnicalDetails}
              size="sm"
              type="button"
              className="btn btn-darken"
              onClick={toggleTechnicalDetails}
            >
              {showTechnicalDetails ? hideDetailsLabel : showDetailsLabel}
            </Button>
            {showTechnicalDetails && (
              <div className="mt-3 text-start" id="layout-error-details">
                <h2>{errorStackLabel}</h2>
                <TechnicalDetailsPre>
                  {error && error.stack ? error.stack : ''}
                </TechnicalDetailsPre>
                <h2 className="mt-3">{componentStackLabel}</h2>
                <TechnicalDetailsPre>
                  {errorInfo && errorInfo.componentStack ? errorInfo.componentStack : ''}
                </TechnicalDetailsPre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

LayoutErrorFallback.propTypes = {
  error: PropTypes.instanceOf(Error),
  errorInfo: PropTypes.shape({
    componentStack: PropTypes.string,
  }),
};

export default LayoutErrorFallback;
